import type { ReactNode } from 'react';

import { Collection } from 'react-aria-components/Collection';
import type { SelectionMode } from 'react-aria-components/GridList';
import { ListBoxLoadMoreItem } from 'react-aria-components/ListBox';

import { ProgressCircle } from '../progress_circle/progress_circle';
import {
  DropdownListBox,
  DropdownListBoxEmptyState,
  DropdownListBoxItem,
  DropdownListBoxItemDescription,
  DropdownListBoxItemText,
} from './dropdown_list_box';

import listBoxStyles from './dropdown_list_box.module.css';

export type DropdownListOption = {
  id: string;
  label: string;
  description?: string;
};

export type DropdownListSelectionMode = SelectionMode;

export type BaseDropdownListProps = {
  options: DropdownListOption[];
  selectionMode: DropdownListSelectionMode;
  size?: 's' | 'm' | 'l';
  optionsListLabel?: string;
  noResultsText?: string;
  loadingText?: string;
  renderOption?: (option: DropdownListOption) => ReactNode;
  isLoading?: boolean;
  onLoadMore?: () => void;
  maxHeight?: number;
};

export type DropdownListContentProps = BaseDropdownListProps;

export function DropdownListContent({
  options,
  selectionMode,
  size = 'm',
  optionsListLabel = 'Options',
  noResultsText = 'No results found',
  loadingText = 'Loading options…',
  renderOption,
  isLoading,
  onLoadMore,
  maxHeight,
}: DropdownListContentProps) {
  const renderDefaultOption = (option: DropdownListOption) => (
    <>
      <DropdownListBoxItemText>{option.label}</DropdownListBoxItemText>
      {option.description ? (
        <DropdownListBoxItemDescription>
          {option.description}
        </DropdownListBoxItemDescription>
      ) : null}
    </>
  );

  return (
    <DropdownListBox
      size={size}
      aria-label={optionsListLabel}
      selectionMode={selectionMode}
      style={maxHeight != null ? { maxHeight } : undefined}
      renderEmptyState={() => (
        <DropdownListBoxEmptyState>
          {isLoading ? (
            <>
              <ProgressCircle
                isIndeterminate
                aria-label={loadingText}
                size="2xs"
                className={listBoxStyles.loading_indicator}
              />
              <DropdownListBoxItemText
                color="medium_emphasis"
                className={listBoxStyles.loading_text}
              >
                {loadingText}
              </DropdownListBoxItemText>
            </>
          ) : (
            <DropdownListBoxItemText color="medium_emphasis">
              {noResultsText}
            </DropdownListBoxItemText>
          )}
        </DropdownListBoxEmptyState>
      )}
    >
      <Collection items={options}>
        {(option) => (
          <DropdownListBoxItem id={option.id} textValue={option.label}>
            {renderOption ? renderOption(option) : renderDefaultOption(option)}
          </DropdownListBoxItem>
        )}
      </Collection>
      {onLoadMore && options.length > 0 ? (
        <ListBoxLoadMoreItem
          isLoading={isLoading}
          onLoadMore={onLoadMore}
          className={listBoxStyles.dropdown_list_box_item}
        >
          <ProgressCircle
            isIndeterminate
            aria-label={loadingText}
            size="2xs"
            className={listBoxStyles.loading_indicator}
          />
          <DropdownListBoxItemText
            color="medium_emphasis"
            className={listBoxStyles.loading_text}
          >
            {loadingText}
          </DropdownListBoxItemText>
        </ListBoxLoadMoreItem>
      ) : null}
    </DropdownListBox>
  );
}
